/**
 * Consumer cache-policy registration.
 *
 * The consumer (Olive) SPA runs its own React Query client (see `App.tsx`). It
 * deliberately does NOT mount the IndexedDB persister the admin app uses - the
 * consumer surface is public/per-visitor and must not write identity or
 * registration data to disk. So this is an IN-MEMORY stale-while-revalidate
 * layer only: search results live in the query cache for the session and repaint
 * instantly when a term is retyped or a picker re-opened, but nothing is
 * persisted across reloads.
 *
 * Like the admin registry (`admin/lib/query-persistence/query-defaults.ts`) this
 * sets per-key-prefix defaults once rather than per hook. React Query resolves
 * defaults by prefix (structural partial match), and options passed by an
 * individual hook always win, so this only sets a floor.
 *
 * SEARCH profile mirrors the admin one (see docs/frontend-caching.md): hold each
 * debounced term's result for 5 min and repaint it instantly on re-type, but
 * still revalidate on mount so a stale/empty term never masks live matches.
 */

import type { QueryClient } from '@tanstack/react-query'

const MINUTE = 60_000

const SEARCH = {
  staleTime: 5 * MINUTE,
  gcTime: 30 * MINUTE,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
} as const

/**
 * Search / type-ahead prefixes on the consumer surface:
 * - `resource-search`   : ENTITY_SEARCH pickers (dealer / retailer list), routed
 *                         through `queryClient.fetchQuery` in the consumer
 *                         resource-lookup providers.
 * - `address-autocomplete` / `address-resolve` : the shared AddressInput lookup,
 *                         routed through `createConsumerAddressLookupApi`.
 * - `consumer-product-search` : the registration wizard's product browse/search.
 */
const SEARCH_PREFIXES: readonly string[] = [
  'resource-search',
  'address-autocomplete',
  'address-resolve',
  'consumer-product-search',
]

/**
 * Register the consumer cache-policy defaults on the QueryClient. Call once at
 * module scope, immediately after the QueryClient is constructed and before any
 * component mounts (defaults are only picked up by the first query of a key).
 */
export function registerConsumerQueryDefaults(queryClient: QueryClient): void {
  for (const prefix of SEARCH_PREFIXES) {
    queryClient.setQueryDefaults([prefix], SEARCH)
  }
}
